import type {
    AnalyticsEvent,
    AnalyticsService,
    AnalyticsTransport,
    AnalyticsUserProperties,
    FeatureFlagEvent,
    TimestampedAnalyticsEvent,
} from '@thinkalpha/ui-ioc/core/types/AnalyticsService';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { multiInject, optional } from 'inversify';
import type { LDEvaluationDetail } from 'launchdarkly-js-sdk-common';
import { inject, injectable } from 'src/features/ioc';

@injectable()
export class AnalyticsServiceImpl implements AnalyticsService {
    // eslint-disable-next-line @thinkalpha/eslint-rules/no-typescript-private-properties -- required pattern
    private logger: Logger;

    constructor(
        @multiInject('AnalyticsTransport') @optional() private transports: AnalyticsTransport[] = [],
        @inject('Logger') baseLogger: Logger,
    ) {
        this.logger = baseLogger.getSubLogger({ name: 'analytics-service' });

        // Log the number of transports initialized
        this.logger.info({
            message: 'Analytics service initialized',
            transports: this.transports.map((transport) => transport.constructor.name),
        });
    }

    track(event: AnalyticsEvent): void {
        // Add timestamp
        const analyticsEvent: TimestampedAnalyticsEvent = {
            ...event,
            timestamp: new Date(),
        };

        if (this.transports.length === 0) {
            if (TRACE) {
                this.logger.debug({
                    message: 'No analytics transports available, discarding event',
                    event: analyticsEvent,
                });
            }
            return;
        }

        // Send event immediately to all transports
        for (const transport of this.transports) {
            transport.sendEvents([analyticsEvent]).catch((err: unknown) => {
                this.logger.error({
                    message: 'Failed to send analytics event to transport',
                    error: err instanceof Error ? err : new Error(String(err)),
                    event: analyticsEvent,
                    transport: transport.constructor.name,
                });
            });
        }

        if (TRACE) {
            this.logger.trace({
                message: `Tracked event: ${event.type}`,
                event: analyticsEvent,
            });
        }
    }

    identify(user: UserDetails, properties?: AnalyticsUserProperties): void {
        if (!user.id) {
            this.logger.error({
                message: 'Cannot identify user without an ID',
                user,
            });
            return;
        }

        for (const transport of this.transports) {
            try {
                transport.setUser(user, properties);
            } catch (err) {
                this.logger.error({
                    message: 'Failed to identify user in transport',
                    error: err instanceof Error ? err : new Error(String(err)),
                    details: { transport: transport.constructor.name },
                });
            }
        }
    }

    clearUser(): void {
        for (const transport of this.transports) {
            try {
                transport.clearUser();
            } catch (err) {
                this.logger.error({
                    message: 'Failed to clear user in transport',
                    error: err instanceof Error ? err : new Error(String(err)),
                    details: { transport: transport.constructor.name },
                });
            }
        }
    }

    addFeatureFlagEvaluation(key: string, value: LDEvaluationDetail): void {
        const featureFlagEvent: Omit<FeatureFlagEvent, 'timestamp'> = {
            type: 'feature_flag',
            key,
            value,
        };

        this.track(featureFlagEvent);
    }
}
